import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { DataManagerService } from '../../../../../data/services/DataManager/data-manager.service';
import { AppConstants } from '../../../../../shared/constants/app-constants';

/** Rows per group; the panel shows a handful, not a page. */
export const RECORDS_PER_GROUP = 5;
/** Default per-request timeout; a provider backed by a slower store can pass its own. */
export const DEFAULT_REQUEST_TIMEOUT_MS = 8000;

/** src=R makes the list endpoints search Redshift (per the backend team). */
export const REDSHIFT_PARAMS: Record<string, string> = { src: 'R' };
/**
 * Redshift answers in seconds, not milliseconds; the 8s default cut /charges off on .io, and 20s
 * cut off name searches of a high-volume account (21-23s in Telescope) that did find the charges.
 */
export const REDSHIFT_TIMEOUT_MS = 30000;

/**
 * GET <service>?limit=&page=1&search=<query>[&extra…] — the same raw `search` param the list screens
 * send (BaseListComponent / DisputesComponent), so the backend's own multi-field search applies.
 * Resolves to the response's `data` rows; any error or timeout resolves to [] so one failing
 * group never blanks the others.
 */
export function listSearch(dataManager: DataManagerService, serviceName: string, query: string,
                           extraParams: Record<string, string> = {},
                           timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS): Observable<any[]> {
  // Keys are developer-defined and may carry the backend's bracket filters (report_date[gte]); values are encoded.
  const extra = Object.keys(extraParams)
    .map(key => '&' + key + '=' + encodeURIComponent(extraParams[key]))
    .join('');
  const url = AppConstants.BASE_URL + serviceName
    + '?limit=' + RECORDS_PER_GROUP + '&page=1&search=' + encodeURIComponent(query) + extra;
  return dataManager.getRequest(url, 0).pipe(
    timeout(timeoutMs),
    map(res => (res && Array.isArray(res.data) ? res.data : [])),
    catchError(() => of([]))
  );
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Formats a dollar amount; '' when not a number. */
export function formatUsd(dollars: any): string {
  const n = Number(dollars);
  return dollars === null || dollars === undefined || dollars === '' || isNaN(n) ? '' : USD.format(n);
}

/** Local date as yyyy/MM/dd — the format the list screens send in date filters. */
export function formatFilterDate(date: Date): string {
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return date.getFullYear() + '/' + pad(date.getMonth() + 1) + '/' + pad(date.getDate());
}

/** Joins the non-empty parts with ' · '. */
export function joinParts(...parts: any[]): string {
  return parts.filter(p => p !== null && p !== undefined && String(p).trim() !== '').join(' · ');
}

/** Masked card number (first6 + last4), shared with the payment details card - see the util. */
export { maskedCardNumber } from '../../../../../shared/utils/masked-card-number';

/**
 * A transaction timestamp as 'MM/DD/YYYY HH:mm' in local time. Accepts a unix-seconds number (what
 * the Redshift path returns), an all-digits string (also seconds), or an ISO date string (MySQL
 * path). '' for anything unparseable.
 */
export function formatTxDateTime(value: any): string {
  if (value === null || value === undefined || value === '') { return ''; }
  let date: Date;
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    date = new Date(Number(value) * 1000); // unix seconds
  } else {
    // Native Date rejects more than 3 fractional-second digits (the MySQL path sends ss.######),
    // so trim to milliseconds first (whatever follows: Z, +00:00, or nothing) or the date is lost.
    date = new Date(String(value).replace(/(\.\d{3})\d+/, '$1'));
  }
  if (isNaN(date.getTime())) { return ''; }
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return pad(date.getMonth() + 1) + '/' + pad(date.getDate()) + '/' + date.getFullYear()
    + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
}
